/**
 * ISO 3166-1 alpha-2 Laenderliste fuer Piloten-Registrierung.
 *
 * Die Liste ist nach "FGL-Prioritaet" sortiert: zuerst der DACH-Raum und
 * direkte Nachbarn (hoechste Pilot-Dichte), danach der Rest alphabetisch.
 */

export interface Country {
  code: string   // ISO 3166-1 alpha-2
  name: string   // Deutsch
  flag: string   // Emoji-Flagge
  priority?: boolean
}

// DACH + direkte Nachbarn zuerst
const PRIORITY: Country[] = [
  { code: 'DE', name: 'Deutschland',   flag: '🇩🇪', priority: true },
  { code: 'AT', name: 'Österreich',    flag: '🇦🇹', priority: true },
  { code: 'CH', name: 'Schweiz',       flag: '🇨🇭', priority: true },
  { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮', priority: true },
  { code: 'LU', name: 'Luxemburg',     flag: '🇱🇺', priority: true },
  { code: 'BE', name: 'Belgien',       flag: '🇧🇪', priority: true },
  { code: 'NL', name: 'Niederlande',   flag: '🇳🇱', priority: true },
  { code: 'IT', name: 'Italien',       flag: '🇮🇹', priority: true },
  { code: 'FR', name: 'Frankreich',    flag: '🇫🇷', priority: true },
  { code: 'CZ', name: 'Tschechien',    flag: '🇨🇿', priority: true },
  { code: 'PL', name: 'Polen',         flag: '🇵🇱', priority: true },
  { code: 'DK', name: 'Dänemark',      flag: '🇩🇰', priority: true },
]

// Alle weiteren Laender alphabetisch (deutsche Namen)
const REST: Country[] = [
  { code: 'AD', name: 'Andorra', flag: '🇦🇩' },
  { code: 'AE', name: 'Vereinigte Arabische Emirate', flag: '🇦🇪' },
  { code: 'AL', name: 'Albanien', flag: '🇦🇱' },
  { code: 'AM', name: 'Armenien', flag: '🇦🇲' },
  { code: 'AR', name: 'Argentinien', flag: '🇦🇷' },
  { code: 'AU', name: 'Australien', flag: '🇦🇺' },
  { code: 'AZ', name: 'Aserbaidschan', flag: '🇦🇿' },
  { code: 'BA', name: 'Bosnien und Herzegowina', flag: '🇧🇦' },
  { code: 'BG', name: 'Bulgarien', flag: '🇧🇬' },
  { code: 'BR', name: 'Brasilien', flag: '🇧🇷' },
  { code: 'BY', name: 'Belarus', flag: '🇧🇾' },
  { code: 'CA', name: 'Kanada', flag: '🇨🇦' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'CO', name: 'Kolumbien', flag: '🇨🇴' },
  { code: 'CY', name: 'Zypern', flag: '🇨🇾' },
  { code: 'EE', name: 'Estland', flag: '🇪🇪' },
  { code: 'EG', name: 'Ägypten', flag: '🇪🇬' },
  { code: 'ES', name: 'Spanien', flag: '🇪🇸' },
  { code: 'FI', name: 'Finnland', flag: '🇫🇮' },
  { code: 'GB', name: 'Vereinigtes Königreich', flag: '🇬🇧' },
  { code: 'GE', name: 'Georgien', flag: '🇬🇪' },
  { code: 'GR', name: 'Griechenland', flag: '🇬🇷' },
  { code: 'HR', name: 'Kroatien', flag: '🇭🇷' },
  { code: 'HU', name: 'Ungarn', flag: '🇭🇺' },
  { code: 'ID', name: 'Indonesien', flag: '🇮🇩' },
  { code: 'IE', name: 'Irland', flag: '🇮🇪' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱' },
  { code: 'IN', name: 'Indien', flag: '🇮🇳' },
  { code: 'IR', name: 'Iran', flag: '🇮🇷' },
  { code: 'IS', name: 'Island', flag: '🇮🇸' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'Südkorea', flag: '🇰🇷' },
  { code: 'KZ', name: 'Kasachstan', flag: '🇰🇿' },
  { code: 'LT', name: 'Litauen', flag: '🇱🇹' },
  { code: 'LV', name: 'Lettland', flag: '🇱🇻' },
  { code: 'MA', name: 'Marokko', flag: '🇲🇦' },
  { code: 'MD', name: 'Moldau', flag: '🇲🇩' },
  { code: 'ME', name: 'Montenegro', flag: '🇲🇪' },
  { code: 'MK', name: 'Nordmazedonien', flag: '🇲🇰' },
  { code: 'MT', name: 'Malta', flag: '🇲🇹' },
  { code: 'MX', name: 'Mexiko', flag: '🇲🇽' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: 'NO', name: 'Norwegen', flag: '🇳🇴' },
  { code: 'NZ', name: 'Neuseeland', flag: '🇳🇿' },
  { code: 'PH', name: 'Philippinen', flag: '🇵🇭' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'RO', name: 'Rumänien', flag: '🇷🇴' },
  { code: 'RS', name: 'Serbien', flag: '🇷🇸' },
  { code: 'RU', name: 'Russland', flag: '🇷🇺' },
  { code: 'SE', name: 'Schweden', flag: '🇸🇪' },
  { code: 'SG', name: 'Singapur', flag: '🇸🇬' },
  { code: 'SI', name: 'Slowenien', flag: '🇸🇮' },
  { code: 'SK', name: 'Slowakei', flag: '🇸🇰' },
  { code: 'SM', name: 'San Marino', flag: '🇸🇲' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
  { code: 'TN', name: 'Tunesien', flag: '🇹🇳' },
  { code: 'TR', name: 'Türkei', flag: '🇹🇷' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
  { code: 'US', name: 'Vereinigte Staaten', flag: '🇺🇸' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾' },
  { code: 'VA', name: 'Vatikanstadt', flag: '🇻🇦' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: 'ZA', name: 'Südafrika', flag: '🇿🇦' },
]

REST.sort((a, b) => a.name.localeCompare(b.name, 'de'))

export const COUNTRIES: Country[] = [...PRIORITY, ...REST]

export function findCountry(code: string | null | undefined): Country | undefined {
  if (!code) return undefined
  return COUNTRIES.find(c => c.code === code.toUpperCase())
}
